import { Form, FormPayload, Newsletter, NewsletterPayload, PreviewPageType, Template, TemplateType } from '@dev/ee-api/ee-api-models';
import { AvailableFeaturesUpselling } from '@dev/ee-modules';
import {
    EditorColor,
    EditorMultiSignUpEditMessage,
    EditorSaveData,
    EditorSaveError,
    EditorTemplateStatus,
    EditorTemplateVersion,
} from '@dev/template-editor';
import { Action } from 'redux';

export enum PaidNewsletterCreatorStatus {
    DEFAULT = 'default',
    LOADING = 'loading',
    LOADED = 'loaded',
    SAVING = 'saving',
    SAVED = 'saved',
    LOAD_ERROR = 'load-error',
    SAVE_ERROR = 'save-error',
}

export type PaidNewsletterErrors = Partial<Record<PaidNewsletterErrorNames, boolean>>;
export enum PaidNewsletterErrorNames {
    DUPLICATED_NAME = 'DuplicatedName',
    INVALID_PRODUCT_PRICE = 'InvalidProductPrice',
    SUBDOMAIN_LANDING_SUBDIRECTORY_IN_USE = 'landing_subdirectory_in_use',
    LACK_OF_ACCOUNT_SUBDOMAIN = 'landing_lack_of_account_subdomain',
    INVALID_SUFFIX = 'landing_invalid_suffix',
    INVALID_FROM_EMAIL = 'InvalidFromEmail',
}

export enum TemplateFormErrorNames {
    NOTIFY_EMAILS_NUMBER_EXCEEDED = 'EmailToNotifyNumberExceeded',
    NOTIFY_EMAILS_NO_PROPER_SEPARATOR = 'EmailToNotifyNotSeparatedBySemicolon',
    NOTIFY_INVALID_EMAILS = 'EmailsToNotifyInvalid',
    NOTIFY_EMAILS_NOT_ON_CONTACT_LIST = 'EmailsToNotifycNotOnContactList',
    FORM_SUBMIT_INVALID_URL = 'FormSubmitInvalidUrl',
    ALREADY_SUBSCRIBED_INVALID_URL = 'AlreadySubscribedInvalidUrl',
    FORM_SUBMIT_NONE_LP = 'FormSubmitNoneLP',
    ALREADY_SUBSCRIBED_NONE_LP = 'AlreadySubscribedNoneLP',
}

export interface EditorMetaDataForm {
    blockClientId: string;
    formName: string;
    formMailingPreferencesLabel: string;
}

export interface EditorTemplateForm {
    blockClientId: string;
    form?: Form;
    formPayload: FormPayload;
    formMailingPreferencesLabel?: string;
    isSaved: boolean;
    isError: boolean;
    isDeleted: boolean;
    isFirstTimeError?: boolean;
    errors?: Partial<Record<TemplateFormErrorNames, boolean>>;
}
export interface EditorMetaData {
    data: {
        contentJson: EditorTemplateVersion;
        recentColors: EditorColor[];
        forms?: EditorMetaDataForm[];

        paymentContentJson?: EditorTemplateVersion; // TEMP
        confirmationContentJson?: EditorTemplateVersion; // TEMP
    };
}

export type SocialPreviewMetaData = {
    imageUrl?: string;
    pageTitle?: string;
    pageDescription?: string;
};

export type PaidNewsletterMetaData = {
    data: {
        socialPreview?: SocialPreviewMetaData;
    };
};

export enum EditorMediaManagerTabIndex {
    IMAGE = 0,
    FILE = 1,
}

export type EditorMediaManagerProps = {
    showImageTabOnly: boolean;
    isLinkToFile: boolean;
    blockId: string;
    isOpen: boolean;
};

export type EditorFile = {
    fileUrl: string;
    fileName: string;
    blockId: string;
    isLinkToFile?: boolean;
};

export type FilesType = {
    filesList: EditorFile[];
    isFileUploading: boolean;
};

export type TemplatePreviewType = {
    templateHtml: string;
};

export interface TemplatesSaveSettings {
    isPublic?: boolean;
    isDefault?: boolean;
    isEmailDefault?: boolean;
    customScreenshot?: string;
}

export interface LoadStartAction extends Action {
    type: PaidNewsletterCreatorActionTypes.LOAD_START;
    payload: {
        name: string;
    };
}
export interface LoadSuccessAction extends Action {
    type: PaidNewsletterCreatorActionTypes.LOAD_SUCCESS;
    payload: {
        paidNewsletterObject: Newsletter;
    };
}

export interface LoadErrorAction extends Action {
    type: PaidNewsletterCreatorActionTypes.LOAD_ERROR;
}
export interface SaveStartAction extends Action {
    type: PaidNewsletterCreatorActionTypes.SAVE_START;
    payload?: {
        data?: EditorSaveData;
        exitOnSave?: boolean;
        errors?: EditorSaveError[];
        isSaveBlocked?: boolean;
        errorsToCheck?: PaidNewsletterErrorNames[];
    };
    meta: {
        thunk: boolean;
    };
}


export interface SaveSuccessAction extends Action {
    type: PaidNewsletterCreatorActionTypes.SAVE_SUCCESS;
    payload: {
        paidNewsletterObject: Newsletter;
        paidNewsletterMetaDataObject: PaidNewsletterMetaData;
        editorSaveData?: EditorSaveData;
    };
    meta: Record<string, unknown>;
}
export interface SaveAllFormsSuccessAction extends Action {
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_FORM_SAVE_ALL_SUCCESS;
    meta: Record<string, unknown>;
}

export interface SaveErrorAction extends Action {
    type: PaidNewsletterCreatorActionTypes.SAVE_ERROR;
    meta: Record<string, unknown>;
    error: boolean;
}
export interface SaveAllFormsErrorAction extends Action {
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_FORM_SAVE_ALL_ERROR;
    meta: Record<string, unknown>;
    error: boolean;
}

export interface SaveEmailTemplateStartAction extends Action {
    type: PaidNewsletterCreatorActionTypes.SAVE_EMAIL_TEMPLATE_START;
    payload: { 
        data?: EditorSaveData;
        exitOnSave?: boolean;
    };
    meta: Record<string, unknown>;
}

export interface SaveEmailTemplateSuccessAction extends Action {
    type: PaidNewsletterCreatorActionTypes.SAVE_EMAIL_TEMPLATE_SUCCESS;
    payload: {
        editorSaveData?: EditorSaveData;
        savedEmailTemplate?: Template;
    };
    meta: Record<string, unknown>;
}

export interface SaveEmailTemplateErrorAction extends Action {
    type: PaidNewsletterCreatorActionTypes.SAVE_EMAIL_TEMPLATE_ERROR;
    meta: Record<string, unknown>;
    error: boolean;
}

export interface SaveSubscriptionCancelationTemplateStartAction extends Action {
    type: PaidNewsletterCreatorActionTypes.SAVE_SUBSCRIPTION_CANCELATION_TEMPLATE_START;
    payload: { 
        data?: EditorSaveData;
        exitOnSave?: boolean;
    };
    meta: Record<string, unknown>;
}

export interface SaveSubscriptionCancelationTemplateSuccessAction extends Action {
    type: PaidNewsletterCreatorActionTypes.SAVE_SUBSCRIPTION_CANCELATION_TEMPLATE_SUCCESS;
    payload: {
        editorSaveData?: EditorSaveData;
        savedSubscriptionCancelationTemplate?: Template;
    };
    meta: Record<string, unknown>;
}

export interface SaveSubscriptionCancelationTemplateErrorAction extends Action {
    type: PaidNewsletterCreatorActionTypes.SAVE_SUBSCRIPTION_CANCELATION_TEMPLATE_ERROR;
    meta: Record<string, unknown>;
    error: boolean;
}

export interface ModifyPaidNewsletterAction extends Action {
    type: PaidNewsletterCreatorActionTypes.MODIFY_PAID_NEWSLETTER;
    payload: {
        paidNewsletterPayload: Partial<NewsletterPayload>;
    };
}

export interface ModifyPaidNewsletterMetaDataAction extends Action {
    type: PaidNewsletterCreatorActionTypes.MODIFY_PAID_NEWSLETTER_METADATA;
    payload: {
        paidNewsletterMetaDataPayload: PaidNewsletterMetaData;
    };
}

export interface StartEditStepAction extends Action {
    type: PaidNewsletterCreatorActionTypes.START_EDIT_STEP;
    payload: {
        stepInEdit: number;
    };
}

export interface CancelEditStepAction extends Action {
    type: PaidNewsletterCreatorActionTypes.CANCEL_EDIT_STEP;
}

export interface CloseAction extends Action {
    type: PaidNewsletterCreatorActionTypes.CLOSE;
}

export interface TemplateUploadFileAction extends Action {
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_UPLOAD_FILE;
    payload: {
        file: File;
        blockId: string;
        isLinkToFile?: boolean;
    };
}

export interface TemplateUploadFileStartAction extends Action {
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_UPLOAD_FILE_START;
}

export interface TemplateUploadFileSuccessAction extends Action {
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_UPLOAD_FILE_SUCCESS;
    payload: {
        filesList: EditorFile[];
    };
}

export interface TemplateUploadFileErrorAction extends Action {
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_UPLOAD_FILE_ERROR;
}

export interface TemplateIncrementAutosaveCounterAction extends Action {
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_INCREMENT_AUTOSAVE_COUNTER;
}

export interface TemplateFormAddNewAction extends Action {
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_FORM_ADD_NEW;
    payload: {
        blockClientId: string;
    };
}
export interface TemplateFormsRemoveAction extends Action {
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_FORMS_REMOVE;
    payload: {
        formsBlockClientIds: string[];
    };
}
export interface TemplateFormSetMailingPreferencesLabelAction extends Action {
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_FORM_SET_MAILING_PREFERENCES_LABEL;
    payload: {
        blockClientId: string;
        formMailingPreferencesLabel?: string;
    };
}
export interface TemplateFormUpdateSavedAction extends Action {
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_FORM_UPDATE_SAVED;
    payload: {
        blockClientId: string;
        form: Form;
    };
}
export interface TemplateFormAddLoadedAction extends Action {
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_FORM_ADD_LOADED;
    payload: {
        blockClientId: string;
        form: Form;
        formMailingPreferencesLabel?: string;
    };
}
export interface TemplateFormDuplicatedAction extends Action {
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_FORM_DUPLICATED;
    payload: {
        blockClientId: string;
        newBlockClientId: string;
    };
}

export interface TemplateFormPayloadUpdateAction extends Action {
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_FORM_PAYLOAD_UPDATE;
    payload: {
        blockClientId: string;
        formPayload: Partial<FormPayload>;
    };
}

export interface TemplateFormsFromReplacedTemplateClearAction extends Action {
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_FORMS_FROM_REPLACED_TEMPLATE_CLEAR;
}

export interface TemplateFormsCheckInvalidAction extends Action {
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_FORMS_CHECK_INVALID;
    payload: {
        blockClientIds: string[];
    };
}

export interface TemplateFormsSetInvalidAction extends Action {
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_FORMS_SET_INVALID;
    payload: {
        blockClientIds: string[];
    };
}

export interface TemplateFormSetErrorsAction extends Action {
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_FORM_SET_ERRORS;
    payload: {
        errors: Partial<Record<TemplateFormErrorNames, boolean>>;
        blockClientId: string;
    };
}

export interface TemplateFormsSetDefaultDoubleOptInDisabledAction extends Action {
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_FORMS_SET_DEFAULT_DOUBLE_OPT_IN;
    payload: {
        isDoubleOptInDisabledByDefault: boolean;
    };
}

export interface OpenUpsellingOverlayAction extends Action {
    type: PaidNewsletterCreatorActionTypes.OPEN_UPSELLING_OVERLAY;
    payload: {
        feature: AvailableFeaturesUpselling;
    };
}
export interface CloseUpsellingOverlayAction extends Action {
    type: PaidNewsletterCreatorActionTypes.CLOSE_UPSELLING_OVERLAY;
}

export interface SetIsTemplateOpenAction extends Action {
    type: PaidNewsletterCreatorActionTypes.SET_IS_TEMPLATE_OPEN;
    payload: {
        isOpen: boolean;
    };
}

export interface SetEmailTemplateOpenTypeAction extends Action {
    type: PaidNewsletterCreatorActionTypes.SET_EMAIL_TEMPLATE_OPEN_TYPE;
    payload: {
        emailTemplateType: EmailTemplateType;
    };
}

export interface SetTemplatePreviewAction extends Action {
    type: PaidNewsletterCreatorActionTypes.SET_TEMPLATE_PREVIEW;
    payload: {
        content?: string;
        name?: string;
        isPublic?: boolean;
    };
}

export interface ResetAction extends Action {
    type: PaidNewsletterCreatorActionTypes.RESET;
}

export interface TemplateShowSaveErrorsAction extends Action {
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_SHOW_SAVE_ERRORS;
}

export interface TemplateSetMultiSignUpEditMessageAction extends Action {
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_SET_MUTLI_SIGN_UP_EDIT_MESSAGE;
    payload: {
        editMessage?: EditorMultiSignUpEditMessage;
    };
}
export interface TemplateChangedAction extends Action {
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_CHANGED;
}

export interface TemplateDeselectAction extends Action {
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_DESELECT;
}

export interface LoadPageTemplateStartAction extends Action {
    type: PaidNewsletterCreatorActionTypes.LOAD_PAGE_TEMPLATE_START;
    payload: {
        pageTemplateType: PreviewPageType;
    };
}
export interface LoadPageTemplateSuccessAction extends Action {
    type: PaidNewsletterCreatorActionTypes.LOAD_PAGE_TEMPLATE_SUCCESS;
    payload: {
        pageTemplateType: PreviewPageType;
        pageTemplate: string;
    };
}

export interface LoadPageTemplateErrorAction extends Action {
    type: PaidNewsletterCreatorActionTypes.LOAD_PAGE_TEMPLATE_ERROR;
}

export interface OpenGalleryAction extends Action {
    type: PaidNewsletterCreatorActionTypes.OPEN_GALLERY;
}

export interface CloseGalleryAction extends Action {
    type: PaidNewsletterCreatorActionTypes.CLOSE_GALLERY;
}

export interface TemplatePreviewFromGalleryAction extends Action {
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_PREVIEW_FROM_GALLERY;
    payload: {
        templateName?: string;
        isPublic?: boolean;
        templateType: TemplateType;
    };
}
export interface TemplateOpenFromGalleryAction extends Action {
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_OPEN_FROM_GALLERY;
    payload: {
        productTemplateName?: string;
        confirmationTemplateName?: string;
        isPublic?: boolean;
    };
}
export interface TemplateSetFromGalleryAction extends Action {
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_SET_FROM_GALLERY;
    payload: {
        productTemplate?: Template;
        confirmationTemplate?: Template;
    };
}

export interface TemplateLoadSingleStartAction extends Action {
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_LOAD_SINGLE_START;
    payload: {
        templateName?: string;
        isPublic?: boolean;
    };
    meta: {
        thunk: boolean;
    };
}

export interface TemplateLoadSingleSuccessAction extends Action {
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_LOAD_SINGLE_SUCCESS;
    payload: {
        template: Template;
    };
    meta: Record<string, unknown>;
}

export interface TemplateLoadSingleErrorAction extends Action {
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_LOAD_SINGLE_ERROR;
    meta: Record<string, unknown>;
    error: boolean;
}

export interface PaidNewsletterCreatorTemplate {
    forms?: EditorTemplateForm[];
    formsFromReplacedTemplate?: EditorTemplateForm[];
    loadedJson?: EditorTemplateVersion;
    paymentLoadedJson?: EditorTemplateVersion;
    confirmationLoadedJson?: EditorTemplateVersion;
    emailLoadedJson?: EditorTemplateVersion;
    subscriptionCancelationLoadedJson?: EditorTemplateVersion;
    status: EditorTemplateStatus;
    recentColors: EditorColor[];
    autosaveCounter: number;
    isNew?: boolean;
    files: FilesType;
    isDoubleOptInDisabledByDefault: boolean;
    multiSignUpEditMessage?: EditorMultiSignUpEditMessage;
}

export interface ModifyEmailTemplateAction extends Action {
    type: PaidNewsletterCreatorActionTypes.MODIFY_EMAIL_TEMPLATE;
    payload: Partial<PostPurchaseTemplatePayload>;
}

export interface ModifySubscriptionCancelationTemplateAction extends Action {
    type: PaidNewsletterCreatorActionTypes.MODIFY_SUBSCRIPTION_CANCELATION_TEMPLATE;
    payload: Partial<SubscriptionCancelationTemplatePayload>;
}

export interface SetPaidNewsletterErrorsAction extends Action {
    type: PaidNewsletterCreatorActionTypes.SET_PAID_NEWSLETTER_ERRORS;
    payload: {
        errors: PaidNewsletterErrors;
    };
}

export interface SetUrlPayloadAction extends Action {
    type: PaidNewsletterCreatorActionTypes.SET_URL_PAYLOAD;
    payload: {
        urlSuffix: string;
    };
}

export interface UpdateTemplatesSaveSettingsAction extends Action {
    type: PaidNewsletterCreatorActionTypes.UPDATE_TEMPLATES_SAVE_SETTINGS;
    payload: Partial<TemplatesSaveSettings>;
}

export interface PostPurchaseTemplatePayload {
    fromEmail?: string;
    fromName?: string;
    subject?: string;
}

export interface SubscriptionCancelationTemplatePayload {
    fromEmail?: string;
    fromName?: string;
    subject?: string;
}

export enum EmailTemplateType {
    EMAIL = 'email',
    SUBSCRIPTION_CANCELATION = 'subscription-cancelation',
}

export interface PaidNewsletterCreatorState {
    stepInEdit?: number;
    status: PaidNewsletterCreatorStatus;
    isTemplateOpen: boolean;
    openEmailTemplateType: EmailTemplateType;
    isTemplateDeselected: boolean;
    
    templatesSaveSettings?: TemplatesSaveSettings;
    template: PaidNewsletterCreatorTemplate;
    templateReplaced?: PaidNewsletterCreatorTemplate;
    
    paidNewsletterObject?: Newsletter;
    paidNewsletterPayload?: Partial<NewsletterPayload>;
    
    nameError?: string;
    
    openUpsellingFeature?: AvailableFeaturesUpselling;


    isAddListError?: boolean;
    templatePreview?: {
        name?: string;
        isPublic?: boolean;
        content?: string;
    };
    isGalleryOpen: boolean;
    
    errors?: PaidNewsletterErrors;
    paidNewsletterMetaDataObject?: PaidNewsletterMetaData;
    paidNewsletterMetaDataPayload?: PaidNewsletterMetaData;

    postPurchaseTemplatePayload?: PostPurchaseTemplatePayload;
    subscriptionCancelationTemplatePayload?: SubscriptionCancelationTemplatePayload;
    urlPayload?: string;

    pageTemplates?: {
        activationViaEmailPage?: string;
        alreadySubscribedPage: string;
        formSubmitPage: string;
        landingPage: string;
    };
}

export interface PublishPaidNewsletterStartAction extends Action {
    type: PaidNewsletterCreatorActionTypes.PUBLISH_PAID_NEWSLETTER_START;
}

export interface PublishPaidNewsletterErrorAction extends Action {
    type: PaidNewsletterCreatorActionTypes.PUBLISH_PAID_NEWSLETTER_ERROR;
}

export enum PaidNewsletterCreatorActionTypes {
    CLOSE = '@@paidNewsletterCreator/CLOSE',
    RESET = '@@paidNewsletterCreator/RESET',

    LOAD_START = '@@paidNewsletterCreator/LOAD_START',
    LOAD_SUCCESS = '@@paidNewsletterCreator/LOAD_SUCCESS',
    LOAD_ERROR = '@@paidNewsletterCreator/LOAD_ERROR',

    START_EDIT_STEP = '@@paidNewsletterCreator/START_EDIT_STEP',
    CANCEL_EDIT_STEP = '@@paidNewsletterCreator/CANCEL_EDIT_STEP',

    MODIFY_PAID_NEWSLETTER = '@@paidNewsletterCreator/MODIFY_PAID_NEWSLETTER',
    MODIFY_PAID_NEWSLETTER_METADATA = '@@paidNewsletterCreator/MODIFY_PAID_NEWSLETTER_METADATA',

    SAVE_START = '@@paidNewsletterCreator/SAVE_START',
    SAVE_SUCCESS = '@@paidNewsletterCreator/SAVE_SUCCESS',
    SAVE_ERROR = '@@paidNewsletterCreator/SAVE_ERROR',

    SAVE_EMAIL_TEMPLATE_START = '@@paidNewsletterCreator/SAVE_EMAIL_TEMPLATE_START',
    SAVE_EMAIL_TEMPLATE_SUCCESS = '@@paidNewsletterCreator/SAVE_EMAIL_TEMPLATE_SUCCESS',
    SAVE_EMAIL_TEMPLATE_ERROR = '@@paidNewsletterCreator/SAVE_EMAIL_TEMPLATE_ERROR',
    
    SAVE_SUBSCRIPTION_CANCELATION_TEMPLATE_START = '@@paidNewsletterCreator/SAVE_SUBSCRIPTION_CANCELATION_TEMPLATE_START',
    SAVE_SUBSCRIPTION_CANCELATION_TEMPLATE_SUCCESS = '@@paidNewsletterCreator/SAVE_SUBSCRIPTION_CANCELATION_TEMPLATE_SUCCESS',
    SAVE_SUBSCRIPTION_CANCELATION_TEMPLATE_ERROR = '@@paidNewsletterCreator/SAVE_SUBSCRIPTION_CANCELATION_TEMPLATE_ERROR',

    OPEN_UPSELLING_OVERLAY = '@@paidNewsletterCreator/OPEN_UPSELLING_OVERLAY',
    CLOSE_UPSELLING_OVERLAY = '@@paidNewsletterCreator/CLOSE_UPSELLING_OVERLAY',

    SET_IS_TEMPLATE_OPEN = '@@paidNewsletterCreator/SET_IS_TEMPLATE_OPEN',
    SET_EMAIL_TEMPLATE_OPEN_TYPE = '@@paidNewsletterCreator/SET_EMAIL_TEMPLATE_OPEN_TYPE',
    
    TEMPLATE_SET_NAME = '@@paidNewsletterCreator/TEMPLATE_SET_NAME',
    TEMPLATE_SET_NAME_ERROR = '@@paidNewsletterCreator/TEMPLATE_SET_NAME_ERROR',
    
    TEMPLATE_INCREMENT_AUTOSAVE_COUNTER = '@@paidNewsletterCreator/TEMPLATE_INCREMENT_AUTOSAVE_COUNTER',
    
    TEMPLATE_FORM_SET_MAILING_PREFERENCES_LABEL = '@@paidNewsletterCreator/TEMPLATE_FORM_SET_MAILING_PREFERENCES_LABEL',
    TEMPLATE_FORM_ADD_NEW = '@@paidNewsletterCreator/TEMPLATE_FORM_ADD_NEW',
    TEMPLATE_FORM_UPDATE_SAVED = '@@paidNewsletterCreator/TEMPLATE_FORM_UPDATE_SAVED',
    TEMPLATE_FORM_ADD_LOADED = '@@paidNewsletterCreator/TEMPLATE_FORM_ADD_LOADED',
    TEMPLATE_FORM_DUPLICATED = '@@paidNewsletterCreator/TEMPLATE_FORM_DUPLICATED',
    TEMPLATE_FORM_PAYLOAD_UPDATE = '@@paidNewsletterCreator/TEMPLATE_FORM_PAYLOAD_UPDATE',
    TEMPLATE_FORMS_FROM_REPLACED_TEMPLATE_CLEAR = '@@paidNewsletterCreator/TEMPLATE_FORMS_FROM_REPLACED_TEMPLATE_CLEAR',
    TEMPLATE_FORMS_REMOVE = '@@paidNewsletterCreator/TEMPLATE_FORMS_REMOVE',
    TEMPLATE_FORMS_CHECK_INVALID = '@@paidNewsletterCreator/TEMPLATE_FORMS_CHECK_INVALID',
    TEMPLATE_FORMS_SET_INVALID = '@@paidNewsletterCreator/TEMPLATE_FORMS_SET_INVALID',
    TEMPLATE_FORMS_SET_DEFAULT_DOUBLE_OPT_IN = '@@paidNewsletterCreator/TEMPLATE_FORMS_SET_DEFAULT_DOUBLE_OPT_IN',
    TEMPLATE_FORM_SET_ERRORS = '@@paidNewsletterCreator/TEMPLATE_FORM_SET_ERRORS',
    TEMPLATE_FORM_SAVE_ALL = '@@paidNewsletterCreator/TEMPLATE_FORM_SAVE_ALL_FORMS',
    TEMPLATE_FORM_SAVE_ALL_SUCCESS = '@@paidNewsletterCreator/TEMPLATE_FORM_SAVE_ALL_FORMS_SUCCESS',
    TEMPLATE_FORM_SAVE_ALL_ERROR = '@@paidNewsletterCreator/TEMPLATE_FORM_SAVE_ALL_FORMS_ERROR',

    TEMPLATE_SET_MUTLI_SIGN_UP_EDIT_MESSAGE = '@@paidNewsletterCreator/TEMPLATE_SET_MUTLI_SIGN_UP_EDIT_MESSAGE',

    TEMPLATE_CHANGED = '@@paidNewsletterCreator/TEMPLATE_CHANGED',

    TEMPLATE_LOAD_SINGLE_START = '@@paidNewsletterCreator/TEMPLATE_LOAD_SINGLE_START',
    TEMPLATE_LOAD_SINGLE_SUCCESS = '@@paidNewsletterCreator/TEMPLATE_LOAD_SINGLE_SUCCESS',
    TEMPLATE_LOAD_SINGLE_ERROR = '@@paidNewsletterCreator/TEMPLATE_LOAD_SINGLE_ERROR',

    TEMPLATE_SHOW_SAVE_ERRORS = '@@paidNewsletterCreator/TEMPLATE_SHOW_SAVE_ERRORS',
    TEMPLATE_DESELECT = '@@paidNewsletterCreator/TEMPLATE_DESELECT',
    TEMPLATE_OPEN_FROM_GALLERY = '@@paidNewsletterCreator/TEMPLATE_OPEN_FROM_GALLERY',
    TEMPLATE_PREVIEW_FROM_GALLERY = '@@paidNewsletterCreator/TEMPLATE_PREVIEW_FROM_GALLERY',
    TEMPLATE_SET_FROM_GALLERY = '@@paidNewsletterCreator/TEMPLATE_SET_FROM_GALLERY',

    SET_TEMPLATE_PREVIEW = '@@paidNewsletterCreator/SET_TEMPLATE_PREVIEW',

    TEMPLATE_UPLOAD_FILE = '@@paidNewsletterCreator/TEMPLATE_UPLOAD_FILE',
    TEMPLATE_UPLOAD_FILE_START = '@@paidNewsletterCreator/TEMPLATE_UPLOAD_FILE_START',
    TEMPLATE_UPLOAD_FILE_SUCCESS = '@@paidNewsletterCreator/TEMPLATE_UPLOAD_FILE_SUCCESS',
    TEMPLATE_UPLOAD_FILE_ERROR = '@@paidNewsletterCreator/TEMPLATE_UPLOAD_FILE_ERROR',

    LOAD_PAGE_TEMPLATE_START = '@@paidNewsletterCreator/LOAD_PAGE_TEMPLATE_START',
    LOAD_PAGE_TEMPLATE_SUCCESS = '@@paidNewsletterCreator/LOAD_PAGE_TEMPLATE_SUCCESS',
    LOAD_PAGE_TEMPLATE_ERROR = '@@paidNewsletterCreator/LOAD_PAGE_TEMPLATE_ERROR',
    
    MODIFY_EMAIL_TEMPLATE = '@@paidNewsletterCreator/MODIFY_EMAIL_TEMPLATE',
    MODIFY_SUBSCRIPTION_CANCELATION_TEMPLATE = '@@paidNewsletterCreator/MODIFY_SUBSCRIPTION_CANCELATION_TEMPLATE',

    OPEN_GALLERY = '@@paidNewsletterCreator/OPEN_GALLERY',
    CLOSE_GALLERY = '@@paidNewsletterCreator/CLOSE_GALLERY',

    SET_PAID_NEWSLETTER_ERRORS = '@@paidNewsletterCreator/SET_PAID_NEWSLETTER_ERRORS',
    
    SET_URL_PAYLOAD = '@@paidNewsletterCreator/SET_URL_PAYLOAD',

    UPDATE_TEMPLATES_SAVE_SETTINGS = '@@paidNewsletterCreator/UPDATE_TEMPLATES_SAVE_SETTINGS',
    
    PUBLISH_PAID_NEWSLETTER_START = '@@paidNewsletterCreator/PUBLISH_PAID_NEWSLETTER_START',
    PUBLISH_PAID_NEWSLETTER_ERROR = '@@paidNewsletterCreator/PUBLISH_PAID_NEWSLETTER_ERROR',
}
